import { useSystemNotify } from "@/components/Chat/SystemNotify";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import useFirstModal from "@/components/FirstModal";
import Footer from "@/components/Footer";
import GdPermission from "@/components/GdPermission";
import GlobalBannerContainer from "@/components/GlobalBanner/Container";
import useGlobalUploader from "@/components/GlobalUploaderModal/useGlobalUploader";
import useImagePreview from "@/components/HBImagePreviewModal/useImagePreview";
import HeaderNew from "@/components/Header/index-new";
import LaunchBottom from "@/components/LaunchBottom";
import Layout from "@/components/Layout";
import { usePinDetail } from "@/components/PinDetail/usePinDetail";
import SideButtonList from "@/components/SideButtonList";
import SideNav from "@/components/SideNav";
import { AD_POSITION } from "@/constants/ads";
import { COOKIE_KEY } from "@/constants/cookieKey";
import {
  ENV,
  getServerSideEnvVariables,
  SERVER_SIDE_ENV_CONFIG_KEY,
} from "@/constants/envConfig";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import { PermissionCodes } from "@/constants/permissionCodes";
import { FlagProvider } from "@/context/flag";
import useAppStore, { useInitStore } from "@/context/store";
import { shallow } from "zustand/shallow";

import {
  getAdsTopConfig,
  getAuthKeyApi,
  getGrocery,
  getMe,
  getTopAds,
} from "@/services";
import initTracker from "@/tracker";
import { calculateHeaderHeight } from "@/utils/headerHeight";
import {
  useDomClassManagement,
  useInitErrorHandler,
  useNavigationTracking,
} from "@/utils/hooks/useAppInitHooks";
import { GuideProvider } from "@/utils/hooks/useNewRegisterGuide";
import { usePageCache } from "@/utils/hooks/usePageCache";
import { useRafIntervalNotice } from "@/utils/hooks/useRafIntervalNotice";
import localStoragePolyfill from "@/utils/localStoragePolyfill";
import { memorizeFn } from "@/utils/memorizeFn";
import { safeParse } from "@/utils/safeParse";
import { initCacheor } from "@/utils/server/cacheor";
import { getGlobalFlag } from "@/utils/server/getFlag";
import { setTransferHeaders } from "@/utils/server/headerTransfer";
import { sidCheck, tokenCheck } from "@/utils/server/sidCheck";
import { stewardInstance } from "@/utils/steward";
import {
  getSideNavCollapsedState,
  useSideNavVisible,
} from "@/utils/useSideNavCollapse";
import { useEventListener } from "ahooks";
import { ConfigProvider } from "antd";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import Script from "next/script";
import { memo, useEffect, useMemo } from "react";

const DownloadList = dynamic(
  import("@/components/MaterialDownloadModal/downloadList"),
  { ssr: false }
);

const BindPhoneGuideFooter = dynamic(
  import("@/components/Footer/BindPhoneGuide"),
  { ssr: false }
);

// 此处必须使用require https://www.npmjs.com/package/next-plugin-antd-less
require("/node_modules/antd/dist/antd.less");
require("../styles/globals.less");
if (typeof window !== "undefined") {
  localStoragePolyfill();
}

const hasFooterPages = [
  { path: "/vip/info", relative: true },
  { path: "/message", relative: false },
  { path: "/login", relative: false },
  { path: "/settings", relative: false },
  { path: "/cooperator/[cooperator]", relative: false },
  { path: "/vip/exchange", relative: false },
  { path: "/activities", relative: true },
  { path: "/activities/start", relative: false },
  { path: "/popular/[urlKey]", relative: true },
  { path: "/coupons/[activityCode]", relative: false },
  { path: "/pages/[[...slug]]", relative: false },
  { path: "/articles", relative: false },
  { path: "/articles/[code]", relative: false },
  { path: "/articles/[code]/[page]", relative: false },
  { path: "/gdmerge", relative: false },
  { path: "/promotions/vip", relative: true },
  { path: "/promotions/618", relative: true },
  { path: "/promotions/pintuan/[code]", relative: true },
  { path: "/landing/demo", relative: true },
];

const purePages = [
  { path: "/login" },
  { path: "/oauth/authorize" },
  { path: "/oauth/callback" },
  { path: "/signup" },
  { path: "/pay/transit" },
  { path: "/password/reset/[token]" },
  { path: "/browser" },
  { path: "/supernice" },
  { path: "/verify" },
  { path: "/share" },
  { path: "/bind/tel" },
  { path: "/gdmerge/callback" },

  {
    path: "/coupons/[activityCode]",
    type: "mobile", // pc、mobile或者不填
  },
  { path: "/vip/consult", type: "mobile" },
  { path: "/vip/buy" },
  { path: "/vip/exchange", type: "mobile" },
];

/**
 * 页面内容组件
 * 独立组件可以让 React 更高效地进行 Diff
 */
const PageContent = memo(function PageContent({
  Component,
  pageProps,
  isPurePage,
  isMobile,
  ads,
  sideNavVisible,
  hasFooter,
  isFooterRelative,
  isHome,
  pathname,
}) {
  const { staticPageConfig } = pageProps;

  if (isPurePage || staticPageConfig?.purePage) {
    return <Component {...pageProps} />;
  }

  /* 导航栏 */
  let headerComponent = (
    <HeaderNew
      isMobile={isMobile}
      searchWord={pageProps?.searchWord}
      ads={ads}
      sideNavVisible={sideNavVisible}
    />
  );

  /* 页脚 */
  let footerComponent = hasFooter && <Footer relative={isFooterRelative} />;

  // 静态页面
  if (staticPageConfig) {
    if (!staticPageConfig.nav) {
      headerComponent = null;
    }

    if (!staticPageConfig.footer) {
      footerComponent = null;
    }
  }

  return (
    <Layout
      header={headerComponent}
      sideNav={<SideNav />}
      topBar={<GlobalBannerContainer />}
      sideNavVisible={sideNavVisible}
      extendContentToHeader={isHome}
    >
      {/* 页面内容 */}
      <Component {...pageProps} />

      {/* 右下角悬浮按钮组 */}
      {!isHome && !["/albums/[urlname]", "/create"].includes(pathname) && (
        <SideButtonList />
      )}

      {/* 左下角下载列表 */}
      <GdPermission code={PermissionCodes.materialLogs}>
        <DownloadList />
      </GdPermission>

      {/* 全局底部登录栏 */}
      <LaunchBottom
        inlinks={pageProps?.inlinks}
        disabledModalOpenWidth={isHome}
      />

      {/* 页脚 */}
      {footerComponent}

      <BindPhoneGuideFooter />
    </Layout>
  );
});

// 判断是否请求的是html结构
function isRequestingHtml(req) {
  const accept = req?.headers?.accept;
  const urlInfo = req?.url;
  // 需要通过两种方式判断用户请求的是否为html，请求头信息有可能会被修改
  return (
    (accept && accept.indexOf("text/html") !== -1) ||
    (urlInfo &&
      !/\.json$/.test(new URL(urlInfo, `https://${req.headers.host}`).pathname))
  );
}

function App(props) {
  const {
    Component,
    pageProps = {},
    os,
    isWeixin,
    isMobile,
    initStore,
    initErr,
  } = props;

  const router = useRouter();
  const pathname = router.pathname;
  const asPath = router.asPath;
  const env = ENV;

  useInitStore(initStore);
  usePageCache();
  useFirstModal();
  const {
    config: appConfig,
    user,
    ads,
    setLastBoards,
    dialogs,
    globalNotice,
  } = useAppStore(
    ({ user, config, setLastBoards, ads, dialogs, globalNotice }) => ({
      user,
      config,
      setLastBoards,
      ads,
      dialogs,
      globalNotice,
    }),
    shallow
  );

  initTracker({ isMobile, user });

  // 使用自定义 Hook 统一处理所有导航追踪逻辑
  useNavigationTracking();

  useRafIntervalNotice(globalNotice);

  const bannerAd = Array.isArray(ads)
    ? ads.find((ad) => ad?.position?.id === AD_POSITION.TOP)
    : null;
  const bannerNotice = globalNotice?.visible && globalNotice?.content;
  const totalHeaderHeight = calculateHeaderHeight({
    hasBannerAd: !!bannerAd,
    hasBannerNotice: !!bannerNotice,
  });

  const isHome = pathname === "/";

  const sideNavVisible = useSideNavVisible(); // 根据页面判断是否需要左侧导航

  // 当totalHeaderHeight变化时更新store中的headerHeight
  useEffect(() => {
    useAppStore.setState({
      headerHeight: totalHeaderHeight,
    });
  }, [totalHeaderHeight]);

  const { hasFooter, isFooterRelative } = useMemo(() => {
    const item = hasFooterPages.find((item) => item.path === pathname);
    return {
      hasFooter: !!item,
      isFooterRelative: item?.relative,
    };
  }, [pathname]);

  const { isPurePage } = useMemo(() => {
    const item = purePages.find((item) => item.path === pathname);
    let isPurePage = !!item;
    if (item?.type === "mobile") {
      isPurePage = isMobile;
    } else if (item?.type === "pc") {
      isPurePage = !isMobile;
    }

    return {
      isPurePage,
    };
  }, [isMobile, pathname]);

  // 使用自定义 Hook 处理初始化错误
  useInitErrorHandler(initErr);

  // 使用自定义 Hook 统一管理 DOM class 操作
  useDomClassManagement({
    os,
    isMobile,
    isWeixin,
    user,
    grayTheme: appConfig?.grayTheme,
    pathname,
  });

  useEventListener("storage", (event) => {
    if (event.key === LOCAL_STORAGE_KEY.UPDATE_LAST_BOARDS) {
      const lastBoards = event.newValue;
      if (lastBoards) {
        try {
          setLastBoards(JSON.parse(lastBoards));
          window.localStorage.removeItem(LOCAL_STORAGE_KEY.UPDATE_LAST_BOARDS);
        } catch (e) {
          console.error("更新最近采集失败");
        }
      }
    }
  });

  useGlobalUploader();
  useSystemNotify();
  useImagePreview();

  useEffect(() => {
    if (user) {
      stewardInstance.init();
    }
  }, [user]);

  usePinDetail();

  function removeCacheParam(url) {
    return url.replace(/([?&])_cache=[^&]*(&|$)/, (match, p1, p2) => {
      // 如果是第一个参数，用 '?' 保留剩余部分；否则用 '&'。
      return p1 === "?" ? (p2 === "&" ? "?" : "") : p2;
    });
  }

  return (
    <>
      <Head>
        <title>
          花瓣网 -
          陪你做生活的设计师（创意图片大全、设计灵感图库、高清图片素材）
        </title>

        <meta charset="utf-8" />
        <meta
          name="keywords"
          content="花瓣网,花瓣,图片大全,创意图片,设计灵感"
        />
        <meta
          name="description"
          content="花瓣网,设计师寻找灵感的平台!这里汇聚海量高清创意图片与设计素材,通过智能采集工具助你高效整理灵感.无论是保存优质素材,规划设计方案,还是分享个人创意收藏,花瓣网都能满足设计师需求,让灵感采集与创作落地无缝衔接。"
        />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta name="renderer" content="webkit" />
        <meta name="copyright" content="huaban.com" />
        <meta name="referrer" content="always" />

        <link rel="apple-touch-icon" href="/img/touch-icon-iphone.png" />
        <link
          rel="apple-touch-icon"
          sizes="76x76"
          href="/img/touch-icon-ipad.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="120x120"
          href="/img/touch-icon-iphone-retina.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="152x152"
          href="/img/touch-icon-ipad-retina.png"
        />
        <link
          rel="canonical"
          // removeCacheParam 避免保存缓存的参数
          href={`https://huaban.com${removeCacheParam(asPath)}`}
        />
        {!isMobile && (
          <meta
            httpEquiv="mobile-agent"
            content={`format=html5;url=https://m.huaban.com${asPath}`}
          />
        )}
      </Head>

      {/* 服务端环境变量注入 */}
      <Script id="env_init" strategy="beforeInteractive">
        {`
          window.${SERVER_SIDE_ENV_CONFIG_KEY} = ${JSON.stringify(
          getServerSideEnvVariables()
        )};
        `}
      </Script>
      {/* gd-service-worker 脚本，加载完成后初始化 */}
      <Script
        src="https://cdn.dancf.com/packages/gaoding/gd-service-worker/v2.7.12/client.huaban.js"
        strategy="afterInteractive"
        onLoad={initServiceWorker}
      />

      {/* 微软统计 */}
      <Script id="ms_clarity" strategy="beforeInteractive">
        {`
          (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "rqhfezlm6u");
        `}
      </Script>

      {/* 百度统计脚本 */}
      <Script
        id="baidu-tongji"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            var _hmt = _hmt || [];
            (function() {
              var hm = document.createElement("script");
              hm.src = "https://hm.baidu.com/hm.js?d4a0e7c3cd16eb58a65472f40e7ee543";
              var s = document.getElementsByTagName("script")[0]; 
              s.parentNode.insertBefore(hm, s);
              hm.onload = function(){
                const htmlEl = document.body.parentElement;
                _hmt.push(["_setUserProperty", {"has_plugin": htmlEl.className.indexOf("hbChromeExtInstalled")>=0?1:0}]);
                _hmt.push(["_setUserProperty", {"plugin_version": htmlEl.getAttribute("data-hb-chrome-ext-installed")}]);
              }
            })();
          `,
        }}
      />
      <Script
        strategy="beforeInteractive"
        src="https://cdn.dancf.com/packages/gaoding/gd-tracker/2.19.0/gd-tracker.browser.js"
      ></Script>
      <Script
        src="https://cdn.dancf.com/packages/gaoding/web-canary-client/v1.8.3/index.umd.js"
        strategy="beforeInteractive"
      ></Script>
      {/* 覆盖web-canary-client白名单，避免所有请求带上相关请求头标识 */}
      <Script
        id="CanaryClientInit"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            if(window.GdWebCanaryClient && window.GdWebCanaryClient.default){
              window.GdWebCanaryClient.default.setWhiteList(["gd.huaban.com", "api${
                env === "prod" ? "" : "-" + env
              }.huaban.com", "${
            env === "prod" ? "" : env + "."
          }huaban.com"], true);
        }
          `,
        }}
      ></Script>
      <Script
        src="https://cdn.dancf.com/packages/gaoding/gd-sls-rum/v1.23.1/index.js"
        strategy="beforeInteractive"
      ></Script>

      {/* 没有特殊用处，纯粹为了让插件的截图功能可以使用 */}
      <div id="header"></div>

      <ConfigProvider autoInsertSpaceInButton={false}>
        <GuideProvider>
          <PageContent
            Component={Component}
            pageProps={pageProps}
            isPurePage={isPurePage}
            isMobile={isMobile}
            ads={ads}
            sideNavVisible={sideNavVisible}
            hasFooter={hasFooter}
            isFooterRelative={isFooterRelative}
            isHome={isHome}
            pathname={pathname}
          />
          {dialogs.map((dialog) => dialog)}
        </GuideProvider>
      </ConfigProvider>
    </>
  );
}

export default function WrapApp(props) {
  return (
    <FlagProvider flag={props.flag || null}>
      <ErrorBoundary>
        <App {...props} />
      </ErrorBoundary>
    </FlagProvider>
  );
}
const getGlobalConfig = memorizeFn(() =>
  getGrocery("global-config", { serverSide: true, timeout: 2500 }).catch(
    () => ({})
  )
);

// 获取全局公告
const getGlobalNotice = memorizeFn(() =>
  getGrocery("global-notice", { serverSide: true, timeout: 2500 }).catch(
    () => ({})
  )
);

// getInitialProps会在客户端运行(路由变化时)，需要确保客户端的兼容性
// https://nextjs.org/docs/api-reference/data-fetching/get-initial-props
WrapApp.getInitialProps = async ({ ctx }) => {
  const { req, res } = ctx;
  const userAgent =
    (typeof window !== "undefined"
      ? window?.navigator?.userAgent
      : req?.headers["user-agent"]) || "";

  const cookie = req?.headers?.["cookie"];
  const host = req?.headers?.["host"];
  const initStore = {};
  let initErr = null;
  let flag = null;
  const isPageCache = res?.isPageCache;

  // 服务端渲染时，设置transferHeaders,透传部分header
  if (typeof window === "undefined") {
    setTransferHeaders(req?.headers);
    const envInfo = require("@/utils/server/envInfo");
    const isSupportWebpByUa = await import("@/utils/isSupportWebpByUa").then(
      (module) => module.default
    );

    Object.assign(initStore, { envInfo: envInfo.default });

    isSupportWebpByUa(userAgent);
  }

  const isWeixin = userAgent.toLowerCase().includes("micromessenger");
  const isMobile =
    getIsMobile(userAgent) ||
    // 匹配移动端域名
    /^(m(-stage|-fat|-dev)?\.)[^.]+\.(com)$/.test(host);

  // 兼容客户端执行情况. 客户端不执行,服务端只有请求html的时候再执行
  if (typeof window === "undefined" && isRequestingHtml(req)) {
    // 获取redis缓存实例
    initCacheor(res);
    const adTopConfig = await getAdsTopConfig();

    const [config, user, ads, globalNotice] = await Promise.all([
      getGlobalConfig(),
      !isPageCache && (sidCheck(cookie) || tokenCheck(cookie))
        ? getMe(cookie)
        : null,
      !isPageCache
        ? getTopAds({
            cookie,
            userAgent,
            url: req?.url,
            adTopConfig,
          })
        : [],
      !isPageCache ? getGlobalNotice() : {},
    ]);

    // 服务端获取ab实验值
    flag = getGlobalFlag(req.abClientData, isPageCache) || null;

    const bannerAd = Array.isArray(ads)
      ? ads.find((ad) => ad?.position?.id === AD_POSITION.TOP)
      : null;
    // 判断是否有全局公告
    const bannerNotice = globalNotice?.visible && globalNotice?.content;
    // 有公告或者广告设置为120，没有则设置为60
    // const headerHeight = bannerAd || bannerNotice ? 120 : 60;
    const hasGlobalBanner = !!(bannerAd || bannerNotice);
    // const adsData = Array.isArray(ads) ? ads?.pop() : null;
    const initImageConfig = safeParse(
      req?.cookies[COOKIE_KEY.IMAGE_URL_CONFIG],
      () => null
    );
    // 计算宽度
    const pageSetting = safeParse(
      req?.cookies[LOCAL_STORAGE_KEY.HUABAN_PAGE_SETTING],
      () => req?.cookies[LOCAL_STORAGE_KEY.HUABAN_PAGE_SETTING]
    );
    const itemWidth = pageSetting?.columnSize || 240;
    const itemGap = 16;
    let num = 20;
    if (pageSetting?.cols) {
      num = pageSetting?.cols;
    }

    // 获取侧边栏收起状态
    const isSideNavCollapsed = getSideNavCollapsedState(req, isPageCache);

    useAppStore.setState({ config });

    Object.assign(initStore, {
      adTopConfig,
      config,
      // 兼容超时情况，超时返回一个request对象
      user: user?.user_id ? user : null,
      userReady: !isPageCache,
      isMobile,
      ads: ads,
      column: num,
      columnSize: itemWidth,
      initImageConfig,
      wrapperWidth: num * (itemWidth + itemGap) - itemGap,
      // headerHeight,
      globalBannerVisible: hasGlobalBanner,
      globalNotice,
      isPageCache,
      // 初始化不显示筛选面板
      isFilterVisible: false,
      isCollapsed: isSideNavCollapsed,
    });

    // 未登录时返回的是404，不需要处理错误信息，其他设备登录时，有status参数，需要处理错误信息
    if (
      (user?.err && user?.err !== 404) ||
      (user?.err === 404 && user?.status === 401)
    ) {
      initErr = user;
    }

    // 拒绝未登录用户访问
    if (config?.refuseNoLogin && !initStore.user) {
      res.writeHead(307, {
        Location: "/unavailable.html",
      });
      return res.end();
    }

    // 拒绝所有访问
    if (config?.refuse) {
      res.writeHead(307, {
        Location: "/unavailable.html",
      });
      return res.end();
    }

    req.user = initStore.user;
  }
  return {
    os: res?.browserInfo?.os || "",
    isWeixin,
    isMobile,
    initStore,
    flag,
    ...(initErr ? { initErr } : {}),
  };
};

function getIsMobile(ua, options) {
  options = options || {};
  if (!ua) return false;

  ua = ua.toLowerCase();
  var isIos = !!ua.match(/ip(?:ad|od|hone)/i);
  var isIpad = !!ua.match(/ipad/i);
  var isAndroid = !!ua.match(/(?:android|okhttp)/i);
  var isUcmobile = ua.match(/(ucbrowser|mobile|ucweb)/gi);
  var isWindowsPhone = ua.match(/(Windows Phone)/gi);
  var isMiPad = ua.match(/(MI PAD)/gi);

  return (
    (isIos || isAndroid || isUcmobile || isWindowsPhone) &&
    (!options.excludePad || (!isIpad && !isMiPad))
  );
}

function initServiceWorker() {
  try {
    if (typeof window === "undefined" || !window.GD_SERVICE_WORKER) {
      return;
    }
    window.GD_SERVICE_WORKER.setOptions({
      authKeyApi: getAuthKeyApi(),
      authMode: "common",
    });
    window.GD_SERVICE_WORKER.register({
      firstInstalled() {
        // 在 sw 第一次注册时触发，重新加载页面，确保sw注册成功
        window.location.reload();
      },
    });
  } catch (error) {
    console.error("initServiceWorker error", error);
  }
}
